import { Box } from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";

import { components } from "@/styles/design-system";

// -- Animations --

const btnShine = keyframes`
  0% { left: -100%; }
  100% { left: 150%; }
`;

// -- Modal Overlay (transparent — shadow ring handles dimming) --

export const Overlay = styled(Box)<{ $open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${components.modal.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  transition: opacity 0.2s ease;
`;

// -- Modal Container (outer, allows close btn overflow) --

export const ModalContainer = styled(Box)<{ $open?: boolean }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 660px;
  width: 92%;
  z-index: 1301;
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};

  @media (max-width: 768px) {
    width: 95%;
  }
`;

// -- Modal Inner --
// Uses the static gradient palette from GradientDialog
// (`Dialog/components.tsx`, .MuiDialog-paper) so this modal reads as the
// same surface as login / signup / cart modals. Keeping the gradient
// inline (rather than reading from design-system tokens) matches the
// authoritative version in Dialog/components.tsx — any tweak to that
// gradient should be mirrored here.

export const ModalInner = styled(Box)<{ $open?: boolean }>`
  background:
    radial-gradient(
      ellipse 45% 40% at 42% -3%,
      rgba(176, 144, 105, 0.9) 0%,
      rgba(160, 128, 90, 0.5) 35%,
      transparent 75%
    ),
    radial-gradient(
      ellipse 55% 50% at 58% 3%,
      rgba(170, 140, 100, 0.75) 0%,
      rgba(140, 110, 78, 0.35) 45%,
      transparent 85%
    ),
    radial-gradient(
      ellipse 70% 35% at 50% 8%,
      rgba(150, 118, 82, 0.4) 0%,
      transparent 70%
    ),
    linear-gradient(
      180deg,
      rgb(153 112 75) 0%,
      rgb(99, 74, 54) 50%,
      rgb(111 83 60) 100%
    );
  border: none;
  border-radius: 24px;
  box-shadow: ${({ $open }) =>
    $open ? "0 0 0 9999px rgba(0, 0, 0, 0.7), 0 20px 60px rgba(0, 0, 0, 0.6)" : "none"};
  overflow: hidden;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity 0.25s ease;

  @media (max-width: 768px) {
    border-radius: 20px;
  }
`;

// -- Close Button (design system tokens) --

export const CloseBtn = styled("button")`
  /* Sits HALF-OUTSIDE the modal's top-right corner as a solid disk (the modal
     body clips its own overflow, but this button is a sibling of it). */
  position: absolute;
  top: -14px;
  right: -14px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid rgba(235, 194, 142, 0.45);
  background: rgb(120, 92, 66);
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
  z-index: 10;
  padding: 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);

  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    fill: none;
    transition: all 0.25s ease;
  }

  &:hover {
    background: rgb(136, 105, 76);
    border-color: rgba(235, 194, 142, 0.6);
    color: #ffffff;
    transform: scale(1.06);
  }

  &:active {
    transform: scale(0.95);
  }

  /* On phones the modal is 95% wide, leaving too little side margin for the
     full -14px overhang — pull the disk in so it stays within the viewport. */
  @media (max-width: 600px) {
    right: -4px;
    top: -12px;
  }
`;

// -- Progress Bar --

export const ProgressBarWrapper = styled(Box)`
  width: 100%;
`;

export const ProgressBar = styled(Box)`
  width: 100%;
  height: 3px;
  background: rgba(235, 194, 142, 0.12);
  position: relative;
`;

export const ProgressFill = styled(Box)<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: linear-gradient(93deg, #ba905c 0%, #ffd29b 50%, #ffe0b1 100%);
  transition: width 0.5s cubic-bezier(0.16, 1, 0.3, 1);
`;

// -- Slider --

export const SliderViewport = styled(Box)<{ $locked?: boolean }>`
  position: relative;
  transition: height 0.45s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: ${({ $locked }) => ($locked ? "hidden" : "visible")};
`;

export const SliderTrack = styled(Box)<{ $sliding: boolean; $offset: number; $locked?: boolean }>`
  display: flex;
  align-items: flex-start;
  width: 200%;
  transform: translateX(${({ $offset }) => $offset}%);
  ${({ $sliding }) => ($sliding ? "transition: transform 0.45s cubic-bezier(0.16, 1, 0.3, 1);" : "transition: none;")}
  /* During a transition, block clicks (no overlapping/desynced slides) + hover
     (the incoming option won't flash as it remounts under the cursor). */
  pointer-events: ${({ $locked }) => ($locked ? "none" : "auto")};
`;

export const SlidePanel = styled(Box)`
  width: 50%;
  flex-shrink: 0;
  padding: 44px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;

  @media (max-width: 768px) {
    padding: 32px 24px;
    gap: 22px;
  }
`;

// -- Content Wrapper --

export const Wrapper = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const ContentWrapper = styled(Box)<{ $isChoiceStep?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
`;

// -- Start Page --

export const StartIcon = styled(Box)`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 1.5px solid rgba(235, 194, 142, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: -12px;

  svg {
    width: 20px;
    height: 20px;
    stroke: #ebc28e;
    fill: none;
    stroke-width: 1.5;
  }
`;

export const StartText = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

// -- Typography --

export const Heading = styled("h2")`
  font-family: "Cormorant Garamond", serif;
  font-size: 1.85rem;
  font-weight: 500;
  color: #ffffff;
  text-align: center;
  margin: 0;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    line-height: 1.2;
  }
`;

export const Description = styled("p")`
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  color: #e0d2c1;
  text-align: center;
  line-height: 1.6;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

export const Caption = styled("p")`
  font-family: "Raleway", sans-serif;
  font-size: 0.85rem;
  color: #b9a089;
  text-align: center;
  line-height: 1.5;
  margin: 0;

  a {
    color: #ebc28e;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: #f7ede6;
      text-decoration: underline;
    }
  }
`;

// -- Step Counter --

export const StepCounter = styled("span")`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 0.75rem;
  /* Peach accent — colors.text.accentPeach in the design system. */
  color: #ffdab9;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

// -- Divider --

export const Divider = styled(Box)`
  width: 100%;
  height: 1px;
  background: rgba(235, 194, 142, 0.15);
`;

// -- Choice Buttons --

export const ChoiceButtonsWrapper = styled(Box)<{ $isDesktop?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 10px;
`;

export const ChoiceButton = styled("button")<{ $isDesktop?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(235, 194, 142, 0.3);
  border-radius: 12px;
  padding: 20px 22px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;

  &:hover {
    border-color: rgba(156, 211, 253, 0.5);
    background: rgba(156, 211, 253, 0.1);

    .radio-circle {
      border-color: #9cd3fd;

      &::after {
        background: #9cd3fd;
      }
    }
  }

  &:active {
    .radio-circle {
      border-color: #9cd3fd;
      box-shadow: 0 0 16px rgba(156, 211, 253, 0.4);

      &::after {
        background: #9cd3fd;
      }
    }
  }
`;

export const RadioCircle = styled("span")`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid rgba(235, 194, 142, 0.35);
  flex-shrink: 0;
  margin-top: 2px;
  transition: border-color 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: transparent;
    transition: background 0.15s ease;
  }
`;
RadioCircle.defaultProps = { className: "radio-circle" };

export const ChoiceContent = styled("div")`
  flex: 1;
`;

export const ChoiceTitle = styled("span")`
  display: block;
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: #fbf4ec;
  margin-bottom: 4px;
`;

export const ChoiceDesc = styled("span")`
  display: block;
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  color: #fbf4ec;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

// -- Result Header (icon + heading inline) --

export const ResultHeader = styled(Box)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  width: 100%;
  box-sizing: border-box;
  padding: 18px 22px;
  border-radius: 14px;
  background: rgba(235, 194, 142, 0.1);
  border: 1px solid rgba(235, 194, 142, 0.25);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);

  & > :first-child {
    position: absolute;
    left: 22px;
    top: 50%;
    transform: translateY(-50%);
  }

  & > :last-child {
    text-align: center;
  }

  /* phone: drop the box + icon entirely — just the heading, centred, no
     overlap with the (now hidden) icon; nudge the heading a touch bigger */
  @media (max-width: 600px) {
    padding: 0;
    background: none;
    border: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;

    & > :first-child {
      position: static;
      transform: none;
    }

    h2 {
      font-size: 1.4rem;
    }
  }
`;

/* Groups the result heading + description so they sit close together, with the
   wider ContentWrapper gap reserved for the space down to the button. On phone
   the heading↔text gap tightens; desktop keeps the original 28px rhythm. */
export const ResultTopGroup = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 14px;

  @media (max-width: 600px) {
    gap: 8px;
  }
`;

const resultVariants = {
  positive: {
    bg: "rgba(52, 211, 153, 0.12)",
    border: "rgba(52, 211, 153, 0.3)",
    stroke: "#34d399",
  },
  negative: {
    bg: "rgba(240, 168, 117, 0.12)",
    border: "rgba(240, 168, 117, 0.3)",
    stroke: "#f0a875",
  },
  error: {
    bg: "rgba(239, 68, 68, 0.12)",
    border: "rgba(239, 68, 68, 0.3)",
    stroke: "#ef4444",
  },
};

export const ResultIcon = styled(Box)<{ $variant: "positive" | "negative" | "error" }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $variant }) => resultVariants[$variant].bg};
  border: 1.5px solid ${({ $variant }) => resultVariants[$variant].border};

  svg {
    width: 20px;
    height: 20px;
    stroke: ${({ $variant }) => resultVariants[$variant].stroke};
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    fill: none;
  }

  /* phone: results show a plain heading only — no icon */
  @media (max-width: 600px) {
    display: none;
  }
`;

// -- Primary Button (gold gradient) --

export const PrimaryButton = styled("button")<{ $fullWidth?: boolean }>`
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "auto")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 14px 28px;
  background: linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%);
  border: none;
  border-radius: 8px;
  color: #3c2d23;
  font-family: "Raleway", sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 0 32px rgba(186, 144, 92, 0.4);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 60%;
    height: 100%;
    background: linear-gradient(120deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 40px rgba(186, 144, 92, 0.6);

    &::before {
      animation: ${btnShine} 0.6s ease-out;
    }
  }

  &:active {
    transform: translateY(0);
  }
`;

// -- "Restart Test" text link (under the result button) --
export const RestartLink = styled("button")`
  /* The result ContentWrapper has a 28px flex gap; pull the link up so it sits
     close under the button (~12px) instead of a full gap + margin. */
  margin: -16px auto 0;
  display: block;
  background: none;
  border: none;
  padding: 4px 8px;
  color: #9cd3fd;
  font-family: "Raleway", sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: 0.3px;
  cursor: pointer;
  opacity: 0.85;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

// -- Secondary Button (outline) --

export const SecondaryButton = styled("button")`
  padding: 14px 28px;
  background: transparent;
  border: 1px solid rgba(235, 194, 142, 0.4);
  border-radius: 8px;
  color: #ebc28e;
  font-family: "Raleway", sans-serif;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;

  &:hover {
    background: rgba(235, 194, 142, 0.1);
    border-color: #ebc28e;
  }
`;

// -- Button Row (for final screens with 2 buttons) --

export const ButtonRow = styled(Box)<{ $isDesktop?: boolean }>`
  display: flex;
  flex-direction: ${({ $isDesktop }) => ($isDesktop ? "row" : "column")};
  gap: ${({ $isDesktop }) => ($isDesktop ? "16px" : "12px")};
  width: 100%;
`;
